import { useState, useRef, useEffect, useMemo, memo } from 'react';
import {
  Phone,
  MessageSquare,
  Mail,
  Radio,
  Pin,
  Check,
  CheckCheck,
  Clock,
  AlertCircle,
  ChevronDown,
  PinOff,
  CalendarClock,
  Ban,
  ArrowDownLeft,
  ContactRound,
  Pencil,
  MessageCircle,
  FileText,
  PhoneMissed,
  PhoneOff,
  StickyNote,
  MailOpen,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatRelativeTime } from '@/lib/utils/format';
import { splitOptOutFooter } from '@/lib/utils/opt-out';
import type { Conversation, Channel } from '@/types/messaging';
import { WhatsAppIcon } from '@/components/icons/whatsapp-icon';
import { InstagramIcon } from '@/components/icons/instagram-icon';
import { TelegramIcon } from '@/components/icons/telegram-icon';
import { useConversationStore, normalizeConvId } from '@/stores/conversation-store';
import { useAuthStore } from '@/stores/auth-store';
import { useSettingsStore } from '@/stores/settings-store';
import { ConvLabelPicker } from './conv-label-picker';
import { ConvAssigneePicker } from './conv-assignee-picker';

const CHANNEL_ICONS: Record<Channel, React.ComponentType<{ className?: string }>> = {
  SMS: MessageSquare,
  MMS: MessageSquare,
  WHATSAPP: WhatsAppIcon,
  VOICE: Radio,
  EMAIL: Mail,
  INTERCOM: MessageCircle,
  INSTAGRAM: InstagramIcon,
  TELEGRAM: TelegramIcon,
};

const CHANNEL_COLORS: Record<Channel, string> = {
  SMS: 'text-blue-600',
  MMS: 'text-blue-600',
  WHATSAPP: 'text-green-600',
  VOICE: 'text-orange-600',
  EMAIL: 'text-purple-600',
  INTERCOM: 'text-blue-600',
  INSTAGRAM: 'text-pink-600',
  TELEGRAM: 'text-sky-500',
};

function MessageStatusIcon({ status, isCancelled, showDeliveryReports = true }: { status: string; isCancelled?: boolean; showDeliveryReports?: boolean }) {
  if (isCancelled) {
    return <Ban className="h-3 w-3 text-muted-foreground shrink-0" />;
  }
  switch (status) {
    case 'queued':
    case 'sending':
      return showDeliveryReports ? <Clock className="h-3 w-3 text-muted-foreground shrink-0" /> : null;
    case 'scheduled':
      return <CalendarClock className="h-3 w-3 text-primary/60 shrink-0" />;
    case 'sent':
      return showDeliveryReports ? <Check className="h-3 w-3 text-muted-foreground shrink-0" /> : null;
    case 'delivered':
      return showDeliveryReports ? <CheckCheck className="h-3 w-3 text-muted-foreground shrink-0" /> : null;
    case 'read':
      return showDeliveryReports ? <CheckCheck className="h-3 w-3 text-blue-500 shrink-0" /> : null;
    case 'failed':
    case 'undelivered':
      return <AlertCircle className="h-3 w-3 text-destructive shrink-0" />;
    default:
      return null;
  }
}

interface ConversationItemProps {
  conversation: Conversation;
  isActive: boolean;
  onClick: () => void;
  /** Hide the inline label chip (kanban view shows label via column) */
  hideLabel?: boolean;
  /** Smaller avatar + tighter padding (used in kanban cards) */
  compact?: boolean;
  /** Don't tint the avatar with the label color — use the default conv color */
  hideLabelColor?: boolean;
  /** Called after the inline label picker changes this conversation's label. */
  onLabelChange?: (label: string) => void;
}

export const ConversationItem = memo(function ConversationItem({
  conversation,
  isActive,
  onClick,
  hideLabel,
  compact,
  hideLabelColor,
  onLabelChange,
}: ConversationItemProps) {
  const Icon = CHANNEL_ICONS[conversation.channel] || Phone;
  const chatLabels = useConversationStore((s) => s.chatLabels);
  const togglePin = useConversationStore((s) => s.togglePin);
  const markAsRead = useConversationStore((s) => s.markAsRead);
  const markAsUnread = useConversationStore((s) => s.markAsUnread);
  const updateConvSubject = useConversationStore((s) => s.updateConvSubject);
  const draftText = useConversationStore((s) => s.drafts[conversation.id]);
  const currentUserEmail = useAuthStore((s) => s.user?.email);
  // Pin state comes from the persisted personal setting, not the conversation
  // object — the many conversation-refresh paths rebuild `conversation` from
  // server docs, which carry no pin field and would drop the flag.
  const isPinned = useSettingsStore((s) => {
    const target = normalizeConvId(conversation.id);
    return s.pinnedConversationIds.some((id) => normalizeConvId(id) === target);
  });
  // Settings › Inbox/Chat View › Delivery Reports — hides the last-message
  // sent/delivered/read tick in the list (same key gates the chat bubbles).
  const showDeliveryReports = useSettingsStore((s) => s.preferences.showDeliveryReports !== false);

  // Assignee-aware unread indicator:
  //   - Conv is assigned to someone OTHER than the current user → render
  //     a small dot (avoid pulling attention to counts on chats this user
  //     isn't expected to handle).
  //   - Conv is unassigned, assigned to "unassigned", or assigned to the
  //     current user → render the numeric badge so the user sees their
  //     own backlog at a glance.
  const assigneeEmail = conversation.assigneeEmail;
  const isAssignedToOther = !!(
    assigneeEmail &&
    assigneeEmail !== 'unassigned' &&
    currentUserEmail &&
    assigneeEmail !== currentUserEmail
  );
  // Manual "Mark as unread" with no real unread messages on top of it:
  // show a dot, not a number. A genuine inbound after the mark pushes
  // the count past 1 and the number takes over again.
  const isManualUnread = !!conversation.markedUnread && conversation.unreadCount <= 1;
  // Pulse the contact display while the CRM phone-lookup pass is in flight
  // for this conversation. Subscribing to a derived boolean keeps the
  // re-render local — other rows don't re-render when this set mutates.
  const isEnriching = useConversationStore((s) => s.enrichingConvIds.has(conversation.id));
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);
  // Resolve display name: subject (contactName, if not just the phone number) > CRM record name > contactName > contactPhone
  const hasRealSubject = conversation.contactName &&
    conversation.contactName !== conversation.contactPhone &&
    conversation.contactName !== `+${conversation.contactPhone}`;
  const displayName = (hasRealSubject ? conversation.contactName : null) || conversation.crmRecordName || conversation.contactName || conversation.contactPhone || 'Unknown';
  const hasCrmRecord = !!(conversation.crmRecordName || conversation.crmRecord);
  const initial = displayName.charAt(0).toUpperCase();

  const lastMsgStatus = conversation.metadata?.lastMessageStatus as string | undefined;
  const lastMsgDirection = conversation.metadata?.lastMessageDirection as string | undefined;
  const lastMsgCancelled = !!(conversation.metadata?.lastMessageCancelled);
  // WhatsApp error 131026 = "Message Undeliverable" — recipient phone may not
  // be a WhatsApp number, hasn't accepted ToS, or has blocked the business.
  // Show a small "not on WhatsApp" badge over the avatar so it's obvious in
  // the conversation list without opening the thread.
  const lastFailedCode = conversation.metadata?.lastFailedCode as number | undefined;
  const notOnWhatsApp = conversation.channel === 'WHATSAPP'
    && lastMsgStatus === 'failed'
    && lastFailedCode === 131026;
  const lastMsgType = conversation.metadata?.lastMessageType as string | undefined;
  const lastMsgIsNote = !!lastMsgType?.includes('--note');
  const lastNoteAuthor = conversation.metadata?.lastNoteAuthor as string | undefined;
  const showStatus = !!lastMsgStatus && lastMsgDirection !== 'inbound' && !lastMsgIsNote;
  const hasDraft = !!(draftText && draftText.trim()) && !isActive;
  const lastMsgIsCall = !!(conversation.metadata?.lastMessageIsCall);
  const lastCallEventType = conversation.metadata?.lastCallEventType as string | undefined;
  const isCallMissed = lastMsgIsCall && (lastCallEventType === 'missed' || lastCallEventType === 'failed' || lastCallEventType === 'rejected');
  const isCallOngoing = lastMsgIsCall && (lastCallEventType === 'ringing' || lastCallEventType === 'accepted');

  const isPlaceholder = conversation.contactName.startsWith('Start ') && conversation.contactName.endsWith(' conversation');

  const labelKey = conversation.label;
  const labelInfo = labelKey && labelKey !== 'null' ? chatLabels[labelKey] || null : null;
  const avatarBgColor = hideLabelColor ? undefined : (labelInfo?.color || undefined);
  const [nowTs] = useState(() => new Date().getTime());

  // Preview enrichment pending (Twilio Firestore-filter path waits for
  // per-conversation Twilio API calls before showing lastMessage + time).
  const enrichmentPending = !!conversation.metadata?.enrichmentPending;

  // 24-hour WhatsApp window remaining indicator
  // Shows a circular progress ring around the avatar that depletes over 24h
  const lastInboundTime = conversation.metadata?.lastInboundTime as number | undefined;
  const windowProgress = useMemo(() => {
    if (conversation.channel !== 'WHATSAPP' || !lastInboundTime) return null;
    const elapsed = nowTs - lastInboundTime;
    const totalMs = 24 * 60 * 60 * 1000;
    const pct = Math.round((elapsed / totalMs) * 100);
    if (pct >= 100) return { pct: 100, exhausted: true };
    if (pct <= 0) return { pct: 0, exhausted: false };
    return { pct, exhausted: false };
  }, [conversation.channel, lastInboundTime, nowTs]);

  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showDropdown]);

  return (
    <div
      data-testid="conversation-item"
      onClick={onClick}
      role="button"
      tabIndex={0}
      className={cn(
        'w-full text-left transition-colors hover:bg-accent cursor-pointer',
        compact ? 'px-2 py-1.5' : 'px-3 pt-3 pb-2',
        isActive && 'bg-accent hover:bg-accent',
        'group'
      )}
    >
      <div className={cn('flex items-center', compact ? 'gap-2' : 'gap-3')}>
        {/* Avatar with optional 24h window ring */}
        <div className={cn('relative shrink-0', compact ? 'h-7 w-7' : 'h-10 w-10')}>
          {conversation.channel === 'WHATSAPP' && !compact && (
            <svg
              className="absolute -inset-[3px] w-[46px] h-[46px]"
              viewBox="0 0 46 46"
              style={{ transform: 'rotate(-90deg)' }}
            >
              <circle
                cx="23" cy="23" r="21"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                className="text-border"
              />
              {windowProgress && !windowProgress.exhausted && (
                <circle
                  cx="23" cy="23" r="21"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  className="text-blue-500"
                  strokeDasharray={`${2 * Math.PI * 21}`}
                  strokeDashoffset={`${(2 * Math.PI * 21) * (windowProgress.pct / 100)}`}
                />
              )}
            </svg>
          )}
          <div
            className={cn(
              'relative flex items-center justify-center rounded-full font-semibold',
              compact ? 'h-7 w-7 text-[11px]' : 'h-10 w-10 text-sm',
              !avatarBgColor && 'bg-primary/10 text-primary',
              notOnWhatsApp && 'opacity-60 grayscale'
            )}
            style={avatarBgColor ? { backgroundColor: avatarBgColor, color: 'white' } : undefined}
            title={notOnWhatsApp ? 'This number may not be on WhatsApp or has blocked your business' : undefined}
          >
            {notOnWhatsApp ? '?' : initial}
            {!compact && (
              <div className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-background border border-border">
                {notOnWhatsApp ? (
                  // Stack the WhatsApp icon under a slash to depict
                  // "not on WhatsApp" without inventing a new asset.
                  <span className="relative inline-flex h-2.5 w-2.5">
                    <Icon className={cn('h-2.5 w-2.5', CHANNEL_COLORS[conversation.channel], 'opacity-60')} />
                    <Ban className="absolute inset-0 h-2.5 w-2.5 text-red-500" strokeWidth={2.5} />
                  </span>
                ) : (
                  <Icon className={cn('h-2.5 w-2.5', CHANNEL_COLORS[conversation.channel])} />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Content — 2 rows only */}
        <div className="flex-1 min-w-0 mt-0.5">
          {/* Row 1: Name + label | time + dropdown */}
          <div className="flex items-center gap-1.5 leading-none min-h-5">
            {isRenaming ? (
              <input
                ref={renameInputRef}
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter') {
                    const trimmed = renameValue.trim();
                    if (trimmed) {
                      updateConvSubject(conversation.id, trimmed);
                    }
                    setIsRenaming(false);
                  } else if (e.key === 'Escape') {
                    setIsRenaming(false);
                  }
                }}
                onBlur={() => {
                  const trimmed = renameValue.trim();
                  if (trimmed && trimmed !== (conversation.contactName || '')) {
                    updateConvSubject(conversation.id, trimmed);
                  }
                  setIsRenaming(false);
                }}
                onClick={(e) => e.stopPropagation()}
                className="text-sm font-semibold leading-none min-w-0 w-full bg-transparent border-b border-primary/40 outline-none py-0.5"
              />
            ) : (
              <span
                data-testid="conversation-name"
                className={cn(
                  'font-semibold truncate leading-none min-w-0 transition-opacity',
                  compact ? 'text-xs' : 'text-sm',
                  conversation.unreadCount > 0 ? 'text-foreground' : 'text-foreground/80',
                  isEnriching && 'animate-pulse opacity-60'
                )}
                title={(() => {
                  // For INTERCOM the contactPhone slot carries an opaque
                  // contact id (not a phone) — surface email/name from
                  // metadata instead, falling back to displayName alone.
                  if (conversation.channel === 'INTERCOM') {
                    const detail = (conversation.metadata?.intercomContactEmail as string | undefined)
                      || (conversation.metadata?.intercomContactName as string | undefined);
                    return detail ? `${displayName} (${detail})` : displayName;
                  }
                  return hasCrmRecord ? `${displayName} (${conversation.contactPhone})` : displayName;
                })()}
              >
                {displayName}
              </span>
            )}
            {hasCrmRecord && (
              <ContactRound className="h-3 w-3 text-primary/60 shrink-0" />
            )}
            {!isPlaceholder && !hideLabel && (
              <div className="shrink-0">
                <ConvLabelPicker
                  conversationId={conversation.id}
                  currentLabel={conversation.label}
                  compact
                  onChange={onLabelChange}
                />
              </div>
            )}
            {/* Pin indicator + time + dropdown trigger */}
            <div ref={dropdownRef} className="ml-auto relative shrink-0 flex items-center gap-1">
              {/* Slides left with the timestamp on hover so the dropdown
                  chevron (absolute, right-0) never lands on top of it. */}
              {isPinned && (
                <Pin className="h-3 w-3 text-muted-foreground shrink-0 rotate-45 transition-transform duration-200 group-hover:-translate-x-5" />
              )}
              <span className={cn(
                'text-muted-foreground whitespace-nowrap transition-transform duration-200 group-hover:-translate-x-5',
                compact ? 'text-[10px]' : 'text-xs'
              )}>
                {enrichmentPending ? '' : formatRelativeTime(conversation.lastMessageTime)}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDropdown(!showDropdown);
                }}
                className="absolute right-0 flex items-center justify-center w-5 h-5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-200 opacity-0 group-hover:opacity-100"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
              {showDropdown && (
                <div className="absolute right-0 top-full mt-1 z-50 min-w-[120px] rounded-md border border-border bg-popover shadow-md py-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePin(conversation.id);
                      setShowDropdown(false);
                    }}
                    className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-popover-foreground hover:bg-accent transition-colors"
                  >
                    {isPinned ? (
                      <>
                        <PinOff className="h-3.5 w-3.5" />
                        Unpin
                      </>
                    ) : (
                      <>
                        <Pin className="h-3.5 w-3.5 rotate-45" />
                        Pin
                      </>
                    )}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDropdown(false);
                      setRenameValue(conversation.contactName || '');
                      setIsRenaming(true);
                      requestAnimationFrame(() => renameInputRef.current?.focus());
                    }}
                    className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-popover-foreground hover:bg-accent transition-colors"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Rename
                  </button>
                  {!isPlaceholder && (
                    // WhatsApp-Web style read toggle. "Mark as unread" shows a
                    // plain unread dot (no count) for the whole team; opening
                    // the chat clears it again.
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDropdown(false);
                        if (conversation.unreadCount > 0) {
                          markAsRead(conversation.id);
                        } else {
                          markAsUnread(conversation.id);
                        }
                      }}
                      className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-popover-foreground hover:bg-accent transition-colors whitespace-nowrap"
                    >
                      {conversation.unreadCount > 0 ? (
                        <>
                          <MailOpen className="h-3.5 w-3.5" />
                          Mark as read
                        </>
                      ) : (
                        <>
                          <Mail className="h-3.5 w-3.5" />
                          Mark as unread
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Row 2: Message preview or Draft */}
          <div className={cn('flex items-center min-h-6', compact ? 'gap-0.5' : 'gap-1.5')}>
            {enrichmentPending ? (
              // Last message still loading from Twilio API — show a skeleton shimmer
              <div className="flex-1 flex items-center gap-1">
                <div className="h-2.5 w-24 rounded-sm bg-muted-foreground/15 animate-pulse" />
              </div>
            ) : hasDraft ? (
              <p className="text-[13px] truncate flex-1 leading-snug">
                <span className="text-red-500 font-medium">Draft: </span>
                <span className="text-muted-foreground">{draftText}</span>
              </p>
            ) : (
              <>
                {lastMsgIsNote && (
                  <StickyNote className="h-3 w-3 shrink-0 text-amber-500" />
                )}
                {!lastMsgIsNote && lastMsgDirection === 'inbound' && !lastMsgCancelled && !lastMsgIsCall && (
                  <ArrowDownLeft className="h-3 w-3 shrink-0 text-blue-600 dark:text-blue-400" />
                )}
                {showStatus && !lastMsgIsCall && <MessageStatusIcon status={lastMsgStatus} isCancelled={lastMsgCancelled} showDeliveryReports={showDeliveryReports} />}
                {lastMsgIsCall && (
                  isCallMissed
                    ? (lastMsgDirection === 'inbound'
                      ? <PhoneMissed className="h-3 w-3 shrink-0 text-red-500" />
                      : <PhoneOff className="h-3 w-3 shrink-0 text-red-500" />)
                    : <Phone className={cn('h-3 w-3 shrink-0', isCallOngoing ? 'text-blue-500' : 'text-emerald-500')} />
                )}
                <p
                  className={cn(
                    'truncate flex-1 leading-snug',
                    compact ? 'text-[11px]' : 'text-[13px]',
                    lastMsgCancelled
                      ? 'text-muted-foreground line-through decoration-muted-foreground/50'
                      : isCallMissed
                      ? 'text-red-500 font-medium'
                      : isCallOngoing
                      ? 'text-blue-500 font-medium'
                      : lastMsgIsCall
                      ? 'text-muted-foreground'
                      : lastMsgDirection === 'inbound'
                      ? 'font-bold text-blue-600 dark:text-blue-400'
                      : conversation.unreadCount > 0
                      ? 'text-foreground font-medium'
                      : 'text-muted-foreground'
                  )}
                  title={conversation.lastMessage || undefined}
                >
                  {labelKey === 'blocked' ? (
                    <>
                      <Ban className="inline h-3 w-3 mr-0.5 -mt-px shrink-0 text-slate-400/60 dark:text-slate-500/60" />
                      <span className="text-slate-400/60 dark:text-slate-500/60 italic">Blocked Contact - Message Hidden</span>
                    </>
                  ) : (
                    <>
                      {conversation.metadata?.lastMessageType === 'template' && (
                        <FileText className="inline h-3 w-3 mr-0.5 -mt-px shrink-0 text-primary/70" />
                      )}
                      {lastMsgIsNote && lastNoteAuthor && (
                        <span className="font-medium text-amber-600 dark:text-amber-400">{lastNoteAuthor}: </span>
                      )}
                      {/* KAN-155: the preview shows the message, not its opt-out footer. */}
                      {(conversation.lastMessage && splitOptOutFooter(conversation.lastMessage).text)
                        || conversation.lastMessage
                        || (lastMsgStatus === 'failed' || lastMsgStatus === 'undelivered' ? 'Failed message' : '')}
                    </>
                  )}
                </p>
              </>
            )}
            {!isPlaceholder && (
              <ConvAssigneePicker
                conversationId={conversation.id}
                currentAssignee={conversation.assigneeEmail}
                compact
                size={compact ? 'sm' : 'md'}
                botActive={!!conversation.activeFlow}
                botLabel={conversation.activeFlow?.flowName}
              />
            )}
            {conversation.unreadCount > 0 && (
              isAssignedToOther || isManualUnread ? (
                // Other-user-assigned: small dot only — count is muted so
                // this user's eyes stay on their own assigned + unassigned
                // chats.
                // Manual "Mark as unread": also a dot — there is no real
                // message count behind it, only a come-back-later reminder.
                // Drawn badge-sized (h-4) so it reads as "unread", not as
                // the muted assigned-to-someone-else marker — the manual
                // flag is a team reminder, so it wins even on chats assigned
                // to another agent.
                <span
                  className={cn(
                    'rounded-full bg-primary shrink-0',
                    isManualUnread
                      ? (compact ? 'h-3 w-3' : 'h-4 w-4')
                      : (compact ? 'h-1.5 w-1.5' : 'h-2 w-2')
                  )}
                  aria-label={
                    isManualUnread
                      ? 'Marked as unread'
                      : `${conversation.unreadCount} unread (assigned to ${assigneeEmail})`
                  }
                />
              ) : (
                <span className={cn(
                  'inline-flex items-center justify-center rounded-full bg-primary font-bold text-primary-foreground shrink-0',
                  compact ? 'h-4 min-w-4 px-1 text-[9px]' : 'h-5 min-w-5 px-1 text-[11px]'
                )}>
                  {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
                </span>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}, (prev, next) => {
  // Only re-render when something the UI actually uses changes
  const p = prev.conversation;
  const n = next.conversation;
  return (
    prev.isActive === next.isActive &&
    p.id === n.id &&
    p.lastMessage === n.lastMessage &&
    p.unreadCount === n.unreadCount &&
    p.markedUnread === n.markedUnread &&
    p.pinned === n.pinned &&
    p.label === n.label &&
    p.assigneeEmail === n.assigneeEmail &&
    // Drives the Bot assignee avatar — without this the row keeps the old
    // avatar when a flow starts or stops.
    p.activeFlow?.flowId === n.activeFlow?.flowId &&
    p.pinnedMessageId === n.pinnedMessageId &&
    p.contactName === n.contactName &&
    p.contactPhone === n.contactPhone &&
    p.crmRecordName === n.crmRecordName &&
    p.crmRecord?.entityId === n.crmRecord?.entityId &&
    p.channel === n.channel &&
    p.metadata?.lastMessageStatus === n.metadata?.lastMessageStatus &&
    p.metadata?.lastMessageDirection === n.metadata?.lastMessageDirection &&
    p.metadata?.lastMessageCancelled === n.metadata?.lastMessageCancelled &&
    p.metadata?.lastMessageIsCall === n.metadata?.lastMessageIsCall &&
    p.metadata?.lastMessageType === n.metadata?.lastMessageType &&
    p.metadata?.lastCallEventType === n.metadata?.lastCallEventType &&
    p.metadata?.lastFailedCode === n.metadata?.lastFailedCode &&
    String(p.lastMessageTime) === String(n.lastMessageTime)
  );
});
